<template>
  <v-container fluid v-if="documentTypeId">
    <div class="d-flex align-center mb-4">
      <v-icon size="28" class="mr-3" color="primary">mdi-file-document</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold">{{ documentType.name }}s</h1>
</div>
      <v-spacer />
      <v-btn color="primary" prepend-icon="mdi-send" @click="showUploadDialog = true" v-show="showSendButton" data-cy="send">
        Send {{ documentType.name }}
      </v-btn>
    </div>
    <div class="mb-2">
      <v-tabs v-model="selectedTab" bg-color="transparent" color="primary" v-if="showDocumentRouteStatusTabs">
        <v-tab :key="0" @click="changeSelectedDocumentRouteStatus(0)">All</v-tab>
        <v-tab
          v-for="ps in routeStatuses"
          :key="ps.id"
          :tab-value="ps.id"
          @click="changeSelectedDocumentRouteStatus(ps.id)"
          >{{ ps.name }}</v-tab
        >
      </v-tabs>
    </div>
    <v-card elevation="0" border>
      <v-data-table-server
        :no-data-text="''"
        :headers="headers"
        :items="documents"
        :items-length="totalRecords"
        :loading="loading"
        :row-props="getRowProps"
        v-model:options="tableOptions"
        :items-per-page-options="[25, 50, 100, { value: -1, title: 'All' }]"
        @update:options="loadPage"
        @click:row="(event, { item }) => selectDocument(item)"
      >
        <template v-slot:item.routeStatusId="{ item }">
          <DocumentRouteStatus :id="item.routeStatusId" />
        </template>

        <template v-for="header in textHeaders" v-slot:[`item.${header.key}`]="{ value }" :key="header.key">
          <div class="text-truncate-cell" :title="value">{{ value }}</div>
        </template>
      </v-data-table-server>
    </v-card>

    <UploadDialog
      :show="showUploadDialog"
      :documentTypeId="documentType.id"
      :cancel="() => (showUploadDialog = false)"
    ></UploadDialog>
  </v-container>
</template>

<script>
import documentService from "@/services/documentService.js";
import documentRouteService from "@/services/documentRouteService.js";
import { mapGetters, mapState } from "vuex";
import UploadDialog from "@/dialogs/UploadDialog.vue";
import dataTableService from "@/services/dataTableService.js";
import documentRouteStatusList from "@/constants/documentRouteStatusList.js";
import documentRouteStatus from "@/constants/documentRouteStatus.js";
import DocumentRouteStatus from "@/components/DocumentRouteStatus.vue";
import metadataKey from "@/constants/metadataKey";
import documentTypeTemplates from "@/constants/documentTypeTemplate";
import Auth0Mixin from "./Auth0Mixin";

// Vuetify emits -1 for the "All" rows-per-page option, but the paged endpoint clamps
// pageSize < 1 to 1 (returning a single record). Instead we request one page large
// enough to return every record.
const ALL_ITEMS_PAGE_SIZE = 2147483647;

export default {
  mixins: [Auth0Mixin],
  name: "DocumentList",
  components: { UploadDialog, DocumentRouteStatus },
  props: {
    documentTypeId: Number,
    routeStatusId: Number,
  },
  data() {
    return {
      showUploadDialog: false,
      staticColumnHeaders: [
        { title: "Doc #", key: "id" },
      ],
      headers: [],
      fetchedRows: [],
      totalRecords: 0,
      loading: true,
      documentType: undefined,
      documentRouteStatusList,
      selectedTab: 0,
      tableOptions: { page: 1, itemsPerPage: 25 },
      headersInitialized: false,
      lastLoadedOptions: null,
    };
  },

  created() {
    this.documentType = this.documentTypes.find(dt => dt.id === this.documentTypeId);
  },

  watch: {
    documentTypeId() {
      this.documentType = this.documentTypes.find(dt => dt.id === this.documentTypeId);
      this.selectedTab = 0;
      this.resetAndLoad();
    },
    routeStatusId() {
      this.resetAndLoad();
    },
  },

  methods: {
    getRowProps({ item }) {
      return { class: item.isRead ? "read-document" : "unread-document" };
    },

    changeSelectedDocumentRouteStatus(selected) {
      this.$router.push({ name: "DocumentList", query: { documentRouteStatusId: selected } });
    },

    resetAndLoad() {
      this.headersInitialized = false;
      this.lastLoadedOptions = null;
      this.tableOptions = { ...this.tableOptions, page: 1 };
      this.loadPage(this.tableOptions);
    },

    async loadPage(options) {
      const showAll = options.itemsPerPage === -1;
      const page = showAll ? 1 : options.page;
      const pageSize = showAll ? ALL_ITEMS_PAGE_SIZE : options.itemsPerPage;

      if (this.lastLoadedOptions) {
        const pageChanged =
          page !== this.lastLoadedOptions.page || pageSize !== this.lastLoadedOptions.pageSize;
        if (!pageChanged) return;
      }

      const previousOptions = this.lastLoadedOptions;
      this.lastLoadedOptions = { page, pageSize };

      this.loading = true;

      try {
        const resultSet = await documentRouteService.getForDocumentTypePaged(
          this.documentTypeId,
          this.routeStatusId,
          page,
          pageSize
        );

        const translated = dataTableService.translateResultSet(resultSet, this.dateFormat);

        if (!this.headersInitialized) {
          const allHeaders = [...this.staticColumnHeaders, ...translated.headers].filter(
            h => h.key !== metadataKey.DivisionOrderResponseStatus
          );
          this.headers = allHeaders;
          this.headersInitialized = true;
        }

        this.fetchedRows = translated.rows;
        this.totalRecords = resultSet.totalRecords ?? 0;
      } catch (error) {
        this.lastLoadedOptions = previousOptions;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    selectDocument(item) {
      this.$router.push({ name: "Document", params: { id: item.id } });
    },

    async resubmitDocument(item) {
      const clone = await documentService.resubmit(item.id);
      this.$router.push({ name: "Document", params: { id: `${clone.id}` } });
    },

    showResubmitButton(item) {
      return item.routeStatusId === documentRouteStatus.Rejected;
    },
  },

  computed: {
    ...mapGetters("userProfile", ["dateFormat"]),
    ...mapState("documentTypeStore", ["documentTypes"]),

    documents() {
      const sortBy = this.tableOptions.sortBy;
      if (!sortBy?.length) return this.fetchedRows;

      const { key, order } = sortBy[0];
      const header = this.headers.find(h => h.key === key);

      return [...this.fetchedRows].sort((a, b) => {
        let result;
        if (header?.sort) {
          result = header.sort(a[key], b[key]);
        } else {
          const av = a[key], bv = b[key];
          result = bv > av ? 1 : bv < av ? -1 : 0;
        }
        return order === "desc" ? result : -result;
      });
    },

    textHeaders() {
      return this.headers.filter(
        h =>
          (h.metadataTypeId === 1 || h.metadataTypeId === 23) &&
          h.key !== "icon" &&
          h.key !== "actions" &&
          h.key !== "routeStatusId"
      );
    },

    showDocumentRouteStatusTabs() {
      var docTypeWithTabs = [documentTypeTemplates.AR_INVOICE, documentTypeTemplates.PENDING_DIVISION_ORDER];
      return this.documentType && docTypeWithTabs.includes(this.documentType.id);
    },
    routeStatuses() {
      var divisionOrderStatuses = [documentRouteStatus.New, documentRouteStatus.Returned];
      var nonInvoiceStatuses = [documentRouteStatus.New, documentRouteStatus.Pending, documentRouteStatus.Returned];
      if (this.documentTypeId === documentTypeTemplates.AR_INVOICE)
        return documentRouteStatusList.filter(x => !nonInvoiceStatuses.includes(x.id));
      if (this.documentTypeId === documentTypeTemplates.PENDING_DIVISION_ORDER)
        return documentRouteStatusList.filter(x => divisionOrderStatuses.includes(x.id));
      return [];
    },
    selectedRouteStatus() {
      if (this.routeStatusId === 0) return 0;
      var ind = this.routeStatuses.findIndex(x => x.id === this.routeStatusId);
      return ind + 1;
    },
    invoiceStatues() {
      return documentRouteStatusList.filter(x => x.id !== documentRouteStatus.New);
    },

    showSendButton() {
      return this.documentTypeId === documentTypeTemplates.AR_INVOICE;
    },
  },
  mounted() {
    if (this.routeStatusId > 0) {
      var ind = this.routeStatuses.findIndex(x => x.id === this.routeStatusId);
      this.selectedTab = ind + 1;
    }
  },
};
</script>
<style>
.unread-document {
  font-weight: bold;
  cursor: pointer;
}
.read-document td {
  background-color: rgb(var(--v-theme-surface-light)) !important;
}
.text-truncate-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
